/**
 * YjsCollaborationProvider
 * WebSocket-based Y.js provider for real-time collaboration
 * 
 * Features:
 * - Real-time document synchronization
 * - User presence awareness
 * - Connection management
 * - Offline support
 */

import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { WEBSOCKET_URL, getWsAuthParams } from '../../api/config';
import { wsTokenRefresher } from '../wsTokenRefresher';
import type {
  CollaborationConfig,
  UserPresence,
  SyncStatus,
  CollaborationEvents,
  YjsProvider,
} from './types';

// Global tracking to prevent multiple providers for the same document
const activeProviders = new Map<string, YjsCollaborationProvider>();

export class YjsCollaborationProvider implements YjsProvider {
  public doc: Y.Doc;
  public status: SyncStatus = 'disconnected';
  public users: Map<string, UserPresence> = new Map();

  private wsProvider?: WebsocketProvider;
  private awareness?: any;
  private config: CollaborationConfig;
  private eventHandlers: Map<keyof CollaborationEvents, Set<Function>> = new Map();
  private isDestroyed = false;

  constructor(config: CollaborationConfig) {
    this.config = config;
    
    // Destroy any existing provider for this document to prevent zombie connections
    const existingProvider = activeProviders.get(config.documentId);
    if (existingProvider && !existingProvider.isDestroyed) {
      console.warn('[YjsCollaborationProvider] Destroying existing provider to prevent duplicate connection', {
        documentId: config.documentId.slice(-8),
      });
      existingProvider.destroy();
    }

    // Use provided doc if it exists (for pre-loading content), otherwise create new
    this.doc = config.doc || new Y.Doc();

    // Register this provider
    activeProviders.set(config.documentId, this);

    // Initialize if not offline
    if (!config.offline) {
      this.initializeProvider();
    }
  }

  /**
   * Initialize WebSocket provider
   */
  private initializeProvider(): void {
    const wsUrl = this.config.websocketUrl || this.getDefaultWebSocketUrl();
    
    try {
      this.wsProvider = new WebsocketProvider(wsUrl, this.config.documentId, this.doc, {
        params: getWsAuthParams(),
        resyncInterval: 30000,
        maxBackoffTime: 30000,
      });
      wsTokenRefresher.register(this.wsProvider);

      // Set up awareness (user presence)
      this.awareness = this.wsProvider.awareness;
      this.setupAwareness();

      // Set up event listeners
      this.setupProviderListeners();

      console.log('[YjsCollaboration] Provider initialized', {
        documentId: this.config.documentId,
        wsUrl,
      });
    } catch (error) {
      console.error('[YjsCollaboration] Failed to initialize provider:', error);
      this.updateStatus('error');
      this.emit('error', error as Error);
    }
  }

  /**
   * Get default WebSocket URL
   */
  private getDefaultWebSocketUrl(): string {
    return WEBSOCKET_URL;
  }

  /**
   * Set up awareness (user presence)
   */
  private setupAwareness(): void {
    if (!this.awareness) return;

    // Set local user state
    this.awareness.setLocalState({
      user: this.config.user,
      cursor: null,
      selection: null,
      lastActive: Date.now(),
    });

    // Listen to awareness changes
    this.awareness.on('change', () => {
      this.updateUsersFromAwareness();
    });
  }

  /**
   * Set up provider event listeners
   */
  private setupProviderListeners(): void {
    if (!this.wsProvider) return;

    // Connection status
    this.wsProvider.on('status', (event: { status: string }) => {
      this.handleStatusChange(event.status);
    });

    // Synced event
    // @ts-ignore - WebsocketProvider types don't include 'synced' event but it exists
    this.wsProvider.on('synced', () => {
      console.log('[YjsCollaboration] Document synced');
      this.updateStatus('synced');
      this.emit('synced');
    });

    // Connection events
    this.wsProvider.on('connection-close', (event: CloseEvent | null) => {
      console.log('[YjsCollaboration] ⚠️ Connection closed', {
        documentId: this.config.documentId.slice(-8),
        closeCode: event?.code,
        closeReason: event?.reason,
        wasClean: event?.wasClean,
        wsUrl: this.config.websocketUrl || this.getDefaultWebSocketUrl(),
        timestamp: new Date().toISOString()
      });
      // Code 1008: server rejected due to connection limit — stop reconnecting immediately
      // to prevent a tight reconnect loop (y-websocket resets its backoff counter on every
      // successful open, keeping retries at 100ms forever when the server keeps rejecting)
      if (event?.code === 1008) {
        console.warn('[YjsCollaboration] Server rejected connection (1008 Too Many Connections) — stopping reconnect');
        this.wsProvider?.disconnect();
        this.emit('error', new Error(`WebSocket rejected: ${event.reason || 'Too many connections'}`));
      }
      this.updateStatus('disconnected');
    });

    // @ts-ignore - Event type mismatch in library
    this.wsProvider.on('connection-error', (error: Error) => {
      console.error('[YjsCollaboration] ❌ Connection error:', {
        documentId: this.config.documentId.slice(-8),
        error: error.message || error,
        errorType: error.constructor.name,
        wsUrl: this.config.websocketUrl || this.getDefaultWebSocketUrl(),
        timestamp: new Date().toISOString()
      });
      this.updateStatus('error');
      this.emit('error', error);
    });
  }

  /**
   * Handle status change
   */
  private handleStatusChange(status: string): void {
    console.log('[YjsCollaboration] 🔄 Status changed:', {
      status,
      documentId: this.config.documentId.slice(-8),
      wsConnected: (this.wsProvider as any)?.wsconnected,
      synced: (this.wsProvider as any)?.synced,
      timestamp: new Date().toISOString()
    });
    
    const mappedStatus: SyncStatus = 
      status === 'connected' ? 'connected' :
      status === 'disconnected' ? 'disconnected' :
      'connecting';
    
    this.updateStatus(mappedStatus);
  }

  /**
   * Update status and notify listeners
   */
  private updateStatus(status: SyncStatus): void {
    if (this.status === status) return;
    
    this.status = status;
    this.emit('status', status);
  }

  /**
   * Update users from awareness
   */
  private updateUsersFromAwareness(): void {
    if (!this.awareness) return;

    const states = this.awareness.getStates();
    const users = new Map<string, UserPresence>();

    states.forEach((state: any, clientId: number) => {
      if (state.user) {
        users.set(state.user.id, {
          id: state.user.id,
          name: state.user.name,
          color: state.user.color,
          cursor: state.cursor || undefined,
          selection: state.selection || undefined,
          lastActive: state.lastActive || Date.now(),
        });
      }
    });

    this.users = users;
    this.emit('users-changed', Array.from(users.values()));
  }

  /**
   * Connect to WebSocket
   */
  public connect(): void {
    if (this.config.offline) {
      console.warn('[YjsCollaboration] Cannot connect in offline mode');
      return;
    }

    if (!this.wsProvider) {
      this.initializeProvider();
    } else {
      this.wsProvider.connect();
    }
  }

  /**
   * Disconnect from WebSocket
   */
  public disconnect(): void {
    if (this.wsProvider) {
      this.wsProvider.disconnect();
      this.updateStatus('disconnected');
    }
  }

  /**
   * Update local user presence
   */
  public updatePresence(presence: Partial<UserPresence>): void {
    if (!this.awareness) return;

    const currentState = this.awareness.getLocalState();
    this.awareness.setLocalState({
      ...currentState,
      ...presence,
      lastActive: Date.now(),
    });
  }

  /**
   * Subscribe to events
   */
  public on<K extends keyof CollaborationEvents>(
    event: K,
    handler: CollaborationEvents[K]
  ): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(handler);
  }

  /**
   * Subscribe to an event once — auto-unsubscribes after first invocation
   */
  public once<K extends keyof CollaborationEvents>(
    event: K,
    handler: CollaborationEvents[K]
  ): void {
    const wrapper = ((...args: any[]) => {
      this.off(event, wrapper as any);
      (handler as any)(...args);
    }) as CollaborationEvents[K];
    this.on(event, wrapper);
  }

  /**
   * Unsubscribe from events
   */
  public off<K extends keyof CollaborationEvents>(
    event: K,
    handler: CollaborationEvents[K]
  ): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  /**
   * Emit event
   */
  private emit<K extends keyof CollaborationEvents>(
    event: K,
    ...args: Parameters<CollaborationEvents[K]>
  ): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          (handler as any)(...args);
        } catch (error) {
          console.error(`[YjsCollaboration] Error in ${event} handler:`, error);
        }
      });
    }
  }

  /**
   * Destroy provider and clean up
   */
  public getDocumentId(): string {
    return this.config.documentId;
  }

  public destroy(): void {
    if (this.isDestroyed) return;

    console.log('[YjsCollaboration] Destroying provider', {
      documentId: this.config.documentId.slice(-8),
      stack: new Error().stack?.split('\n').slice(2, 5).join('\n') // Show call stack
    });

    // Unregister from global tracking
    activeProviders.delete(this.config.documentId);

    // Disconnect WebSocket
    if (this.wsProvider) {
      wsTokenRefresher.unregister(this.wsProvider);
      this.wsProvider.destroy();
      this.wsProvider = undefined;
    }

    // Clear awareness
    if (this.awareness) {
      this.awareness.destroy();
      this.awareness = undefined;
    }

    // Clear event handlers
    this.eventHandlers.clear();

    // Clear users
    this.users.clear();

    this.isDestroyed = true;
    this.updateStatus('disconnected');
  }

  /**
   * Get Y.js fragment for ProseMirror
   */
  public getFragment(name: string = 'prosemirror'): Y.XmlFragment {
    return this.doc.getXmlFragment(name);
  }

  /**
   * Get Y.js map
   */
  public getMap(name: string): Y.Map<any> {
    return this.doc.getMap(name);
  }

  /**
   * Get Y.js array
   */
  public getArray(name: string): Y.Array<any> {
    return this.doc.getArray(name);
  }

  /**
   * Get Y.js text
   */
  public getText(name: string): Y.Text {
    return this.doc.getText(name);
  }
}

/**
 * Create a collaboration provider
 */
export function createCollaborationProvider(
  config: CollaborationConfig
): YjsCollaborationProvider {
  return new YjsCollaborationProvider(config);
}

